import React, { useMemo } from 'react';
import { styled } from 'baseui';
import { Layer } from 'baseui/layer';
import type { GetULabelConfigFunction } from '../../../../../common/providers/feature-flag-provider';
import type { ContentRect } from '../../../../../common/types/content-rect';
import { VideoThumbnailContainer } from '../../../../labelling/components/editor/video-thumbnail-container';
import type { MetaDataT } from '../../../../labelling/types';
import { useImageUrlForFrameIndex } from '../../../atoms/annotation-atoms';
import { FRAME_PREVIEW_SCALE } from '../constants';
const Container = styled<'div', {
  $height?: number;
  $width?: number;
  $scale?: number;
}>('div', ({
  $theme,
  $height,
  $width,
  $scale = FRAME_PREVIEW_SCALE
}) => ({
  position: 'absolute',
  bottom: 0,
  left: 0,
  height: `${$height}px`,
  width: `${$width}px`,
  border: `solid ${Number($theme.sizing.scale100.split('px')[0]) * $scale}px ${$theme.colors.borderOpaque}`,
  overflow: 'hidden',
  display: 'flex',
  alignItems: 'end'
}));
const HoveredImage = styled('img', {
  width: '100%',
  height: '100%'
});
type PropsT = {
  isVideoPlayer: boolean;
  getULabelConfig: GetULabelConfigFunction | null;
  imageMetaData: MetaDataT;
  container: HTMLDivElement;
  containerDimensions: ContentRect;
  hoveredFrame: number;
};
export function HoveredFrame({
  isVideoPlayer,
  getULabelConfig,
  imageMetaData,
  container,
  containerDimensions,
  hoveredFrame
}: PropsT) {
  const hoveredImageUrl = useImageUrlForFrameIndex(hoveredFrame);
  const thumbnailDimensions = useMemo(() => {
    const aspectRatio = imageMetaData.width / imageMetaData.height;
    const scale = FRAME_PREVIEW_SCALE;
    if (containerDimensions.width < containerDimensions.height) {
      return {
        width: containerDimensions.width * scale,
        height: containerDimensions.width * scale / aspectRatio
      };
    } else {
      return {
        width: containerDimensions.height * scale * aspectRatio,
        height: containerDimensions.height * scale
      };
    }
  }, [containerDimensions.width, containerDimensions.height, imageMetaData.width, imageMetaData.height]);
  if (hoveredImageUrl) {
    return <Layer mountNode={container} isHoverLayer={true}>
        <Container data-testid="frame_preview" $height={thumbnailDimensions.height} $width={thumbnailDimensions.width}>
          <HoveredImage src={hoveredImageUrl} alt="labelling-image" />
        </Container>
      </Layer>;
  }
  if (isVideoPlayer) {
    return <Layer mountNode={container} isHoverLayer={true}>
        <Container data-testid="frame_preview" $height={thumbnailDimensions.height} $width={thumbnailDimensions.width}>
          <VideoThumbnailContainer getULabelConfig={getULabelConfig ?? null} hoveredFrame={hoveredFrame} />
        </Container>
      </Layer>;
  }
  return null;
}